import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, of, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Respuesta del endpoint `GET /files/signed-url`.
 *
 *  - `url`: URL temporal pre-signed por S3, lista para usar en `<img>`,
 *    `<a href>` o descargas. NUNCA log-uear esta URL completa porque
 *    incluye la firma — alguien con la URL puede descargar el objeto.
 *  - `expiresAt`: ISO 8601 — cuando expira la firma. El front cachea
 *    hasta `expiresAt - 60s` para evitar usar URLs vencidas.
 */
export interface SignedUrlResponse {
  url: string;
  expiresAt: string;
}

/**
 * Respuesta del endpoint `POST /files/upload-url`.
 *
 *  - `url`: URL pre-signed para hacer `PUT` directo a S3 desde el browser
 *    sin pasar por el backend. El cliente debe enviar el `Content-Type`
 *    exacto que se pidió, sino S3 rechaza la firma con 403
 *    `SignatureDoesNotMatch`.
 *  - `key`: la key final donde queda el objeto — guardarla en BD vía
 *    el endpoint del recurso (no acá).
 *  - `expiresAt`: TTL ~10 min — subir el archivo enseguida.
 */
export interface UploadUrlResponse {
  url: string;
  key: string;
  expiresAt: string;
}

/**
 * Identifica al usuario **dueño** del archivo cuando el JWT actual NO
 * es el dueño (caso típico: un operador o cajero retail subiendo un
 * archivo en nombre del cliente final). El backend valida que el JWT
 * tenga rol de operador para aceptar este parámetro; un cliente común
 * recibe 403 si lo envía.
 *
 * Al menos uno de los campos debe estar poblado. Si vienen varios, el
 * backend usa la precedencia: `transactionId` → `cognitoId` → `email` →
 * `userId`.
 *
 * El path resultante contiene el `cognitoId` del usuario referenciado,
 * NO el del JWT, preservando la convención `<purpose>/<cognitoId>/...`
 * para vinculación de archivos al usuario dueño.
 */
export interface UploadOnBehalfOf {
  /** ID interno de la tabla `transaccion`. Backend resuelve `→ user.cognito_id`. */
  transactionId?: number;
  /** ID interno de la tabla `user`. */
  userId?: number;
  /** Cognito ID directo del cliente. */
  cognitoId?: string;
  /** Email del usuario en Cognito. Backend resuelve `→ user.cognito_id`. */
  email?: string;
}

/**
 * Shape estándar del body de error que devuelven los endpoints `/files/*`.
 * El campo `allowed` solo aparece en `400 Unsupported contentType` —
 * permite al caller mostrarle al usuario qué tipos puede subir.
 */
export interface S3UrlErrorBody {
  statusCode: number;
  message: string;
  allowed?: string[];
}

/**
 * Servicio único para obtener pre-signed URLs de S3 desde el backend.
 *
 * Reemplaza el patrón vulnerable de hacer `new S3({ accessKeyId,
 * secretAccessKey })` directo en el browser (ver VULN-01 de
 * VULNERABILIDADES.md). Las credenciales AWS NUNCA viajan al cliente —
 * el backend firma con su IAM role server-side.
 *
 * Patrón de uso:
 *   const url = await this.s3UrlService.getReadUrl(user.fotoIdfrontal);
 *   // url es null si la key está vacía o el backend respondió 404.
 *
 * Cache en memoria por instancia: las firmas son estables durante su
 * TTL (1h por default backend), no hay razón de re-pegar al backend en
 * cada `<img>` render o cambio de filtro.
 */
@Injectable({
  providedIn: 'root',
})
export class S3UrlService {
  private readonly base = `${environment.apiNest}files`;

  /**
   * Cache de URLs firmadas. Key = S3 key relativa.
   * Value = { url firmada, timestamp en el que expira }.
   * Se invalida automáticamente cuando faltan menos de 60s al expiry.
   */
  private readonly readCache = new Map<
    string,
    { url: string; expiresAt: number }
  >();

  constructor(private http: HttpClient) {}

  /**
   * Obtiene una URL pre-signed de **lectura** (GET) para una S3 key.
   *
   * Pensado para `<img>`, descargas o `<a href>`. El backend valida que
   * el usuario logueado tiene permiso sobre la key — el frontend NO
   * necesita firmar nada.
   *
   * Handling de errores alineado al contrato backend:
   *
   *  | HTTP | Tratamiento aquí | Por qué |
   *  |---|---|---|
   *  | 404 | `null` silencioso | objeto no existe → UI placeholder |
   *  | 403 | `null` silencioso | scope inválido — NO es sesión, NO logout |
   *  | 429 | `null` + warn      | rate-limited; caller debería backoffear |
   *  | 401 | propaga             | token roto — interceptor global hace logout |
   *  | 5xx | `null` + warn      | error transitorio del backend |
   *
   * Es async por diseño: el patrón legacy era síncrono porque firmaba
   * en el browser sin red. Migrar a async fuerza al caller a esperar
   * el round-trip, pero el cache hace que repeticiones sobre la misma
   * key sean inmediatas.
   */
  async getReadUrl(
    key: string | null | undefined,
    bucket?: 'wallet' | 'billing',
  ): Promise<string | null> {
    if (!key || typeof key !== 'string' || !key.trim()) return null;
    const cleanKey = key.trim();
    const bucketKey = bucket ?? 'wallet';
    const cacheKey = `${bucketKey}::${cleanKey}`;

    const cached = this.readCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now() + 60_000) {
      return cached.url;
    }

    let params = new HttpParams().set('key', cleanKey);
    if (bucket) params = params.set('bucket', bucket);

    try {
      const res = await firstValueFrom(
        this.http
          .get<SignedUrlResponse>(`${this.base}/signed-url`, { params })
          .pipe(
            catchError((err: HttpErrorResponse) => {
              // 404 (objeto inexistente) y 403 (scope inválido) son
              // estados esperables — silenciar y caer al placeholder.
              if (err.status === 404 || err.status === 403) {
                return of(null);
              }
              return throwError(() => err);
            }),
          ),
      );
      if (!res) return null;
      this.readCache.set(cacheKey, {
        url: res.url,
        expiresAt: new Date(res.expiresAt).getTime(),
      });
      return res.url;
    } catch (error) {
      const status = (error as HttpErrorResponse)?.status;
      // 401 ya lo está procesando el interceptor global (logout). No
      // necesitamos warn duplicado; igual devolvemos null para que la
      // UI no rompa mientras se ejecuta el redirect.
      if (status === 401) return null;
      if (status === 429) {
        console.warn(
          '[S3UrlService] Rate limit alcanzado en /files/signed-url (60/min). Aplicar backoff.',
        );
        return null;
      }
      // 5xx / red — log para diagnóstico, placeholder en UI.
      console.warn('[S3UrlService] Error firmando URL de lectura', error);
      return null;
    }
  }

  /**
   * Versión bulk: trae varias URLs en una sola pasada y devuelve un
   * `Record<key, url|null>`. Útil cuando una pantalla muestra muchas
   * fotos (ej: lista de compliance, modal con frontal + dorso + selfie).
   *
   * Conserva el orden e incluye claves vacías como `null` sin pegarle
   * al backend.
   */
  async getReadUrls(
    keys: Array<string | null | undefined>,
  ): Promise<Record<string, string | null>> {
    const unique = Array.from(
      new Set(
        keys
          .filter((k): k is string => typeof k === 'string' && !!k.trim())
          .map((k) => k.trim()),
      ),
    );
    const pairs = await Promise.all(
      unique.map(async (k) => [k, await this.getReadUrl(k)] as const),
    );
    const out: Record<string, string | null> = {};
    for (const [k, v] of pairs) out[k] = v;
    return out;
  }

  /**
   * Obtiene una URL pre-signed de **escritura** (PUT) para subir un
   * archivo desde el browser directo a S3, sin que el binario pase por
   * el backend. El caller debe usar exactamente el `contentType` que
   * envió en el body — S3 rechaza la firma si difiere.
   *
   * El backend decide la key final. Por default, la key se ubica bajo
   * el `cognitoId` del JWT actual (`<purpose>/<cognitoId-JWT>/...`).
   * Si el JWT pertenece a un operador que sube en nombre de otro
   * usuario, pasar `onBehalfOf` para que el path quede bajo el cognitoId
   * del cliente dueño del archivo (no del operador que sube).
   *
   * Purposes:
   *  - `kyc`: KYC del propio usuario (docs de identidad, selfie).
   *  - `avatar`: foto de perfil.
   *  - `document`: documentos genéricos del usuario.
   *  - `comprobante`: comprobante de pago de una transacción. Folder
   *    raíz `comprobantes/` (en lugar de `documents/`) y nombre exacto
   *    del `suggestedName` (sin timestamp prefijado por backend).
   *    Requiere `onBehalfOf` con `transactionId` o `userId` y rol
   *    operador/cajero retail.
   */
  getUploadUrl(
    contentType: string,
    purpose: 'kyc' | 'avatar' | 'document' | 'comprobante',
    suggestedName?: string,
    onBehalfOf?: UploadOnBehalfOf,
  ): Promise<UploadUrlResponse> {
    const body: Record<string, unknown> = { contentType, purpose };
    if (suggestedName !== undefined) body['suggestedName'] = suggestedName;
    if (onBehalfOf && Object.keys(onBehalfOf).length > 0) {
      body['onBehalfOf'] = onBehalfOf;
    }
    return firstValueFrom(
      this.http.post<UploadUrlResponse>(`${this.base}/upload-url`, body),
    );
  }

  /** Invalida una entrada del cache (ej: tras subir un reemplazo). */
  invalidate(key: string): void {
    this.readCache.delete(key);
  }

  /** Limpia todo el cache — útil tras logout. */
  clear(): void {
    this.readCache.clear();
  }
}
